/* eslint-disable */
/**
 * WatchlistPage — Track assets you're watching but haven't bought yet
 *
 * Props:
 *   supabase   : supabase client
 *   user       : current user object
 *   isMobile   : boolean
 */
import { useState, useEffect, useRef } from "react";
import React from "react";
import { C, fmt$ } from "../shared/globals.js";

const FM = "Montserrat,sans-serif";

const POPULAR_STOCKS = [
  "AAPL","MSFT","NVDA","TSLA","AMZN","GOOGL","META","MSTR","COIN","PLTR",
  "AMD","NFLX","DIS","BABA","UBER","SHOP","SNOW","RBLX","SOFI","RIVN",
];
const POPULAR_CRYPTO = [
  { ticker:"BTC",  yahoo:"BTC-USD",  name:"Bitcoin" },
  { ticker:"ETH",  yahoo:"ETH-USD",  name:"Ethereum" },
  { ticker:"SOL",  yahoo:"SOL-USD",  name:"Solana" },
  { ticker:"BNB",  yahoo:"BNB-USD",  name:"BNB" },
  { ticker:"XRP",  yahoo:"XRP-USD",  name:"XRP" },
  { ticker:"ADA",  yahoo:"ADA-USD",  name:"Cardano" },
  { ticker:"AVAX", yahoo:"AVAX-USD", name:"Avalanche" },
  { ticker:"DOGE", yahoo:"DOGE-USD", name:"Dogecoin" },
  { ticker:"DOT",  yahoo:"DOT-USD",  name:"Polkadot" },
  { ticker:"LINK", yahoo:"LINK-USD", name:"Chainlink" },
  { ticker:"RUNE", yahoo:"RUNE-USD", name:"THORChain" },
  { ticker:"MATIC",yahoo:"MATIC-USD",name:"Polygon" },
];

const fmtPct = v => (v >= 0 ? "+" : "") + v.toFixed(2) + "%";

const fmtPrice = v => {
  if (!v && v !== 0) return "—";
  if (v < 0.001)  return "$" + v.toFixed(6);
  if (v < 0.01)   return "$" + v.toFixed(5);
  if (v < 1)      return "$" + v.toFixed(4);
  if (v < 10)     return "$" + v.toFixed(3);
  return fmt$(v);
};

// ── Add/Edit Modal ────────────────────────────────────────────────────────────
function WatchlistModal({ item, onSave, onClose }) {
  const [ticker, setTicker]       = useState(item?.ticker || "");
  const [name, setName]           = useState(item?.name || "");
  const [assetType, setAssetType] = useState(item?.asset_type || "stock");
  const [targetPrice, setTargetPrice] = useState(item?.target_price || "");
  const [alertPrice, setAlertPrice]   = useState(item?.alert_price || "");
  const [alertDir, setAlertDir]       = useState(item?.alert_direction || "above");
  const [note, setNote]               = useState(item?.note || "");
  const [saving, setSaving]           = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [fetchedPrice, setFetchedPrice] = useState(null);
  const [fetchingPrice, setFetchingPrice] = useState(false);
  const inputRef = useRef(null);

  const cryptoMatch = POPULAR_CRYPTO.find(c =>
    c.ticker === ticker.toUpperCase() || c.yahoo === ticker.toUpperCase()
  );
  const yahooTicker = assetType === "crypto"
    ? (cryptoMatch?.yahoo || ticker.toUpperCase() + "-USD")
    : ticker.toUpperCase();

  // Filter suggestions based on input
  const suggestions = ticker.length >= 1 ? (
    assetType === "crypto"
      ? POPULAR_CRYPTO.filter(c =>
          c.ticker.startsWith(ticker.toUpperCase()) ||
          c.name.toLowerCase().startsWith(ticker.toLowerCase())
        ).slice(0, 6)
      : POPULAR_STOCKS.filter(s =>
          s.startsWith(ticker.toUpperCase())
        ).slice(0, 6).map(s => ({ ticker: s, name: s }))
  ) : [];

  const selectSuggestion = async (s) => {
    const t = s.ticker || s;
    const n = s.name || s;
    setTicker(t);
    setName(n);
    setShowDropdown(false);
    // Fetch live price
    setFetchingPrice(true);
    setFetchedPrice(null);
    try {
      const yahoo = s.yahoo || (assetType === "crypto" ? t + "-USD" : t);
      const res = await fetch(`/api/mstr-price?ticker=${encodeURIComponent(yahoo)}&t=${Date.now()}`);
      if (res.ok) {
        const d = await res.json();
        if (d.price) {
          setFetchedPrice(d.price);
          // alert price stays empty — user fills manually
        }
      }
    } catch {}
    setFetchingPrice(false);
  };

  const handleTickerChange = (val) => {
    setTicker(val.toUpperCase());
    setShowDropdown(true);
    // Auto-fill name for crypto
    const match = POPULAR_CRYPTO.find(c => c.ticker === val.toUpperCase());
    if (match) setName(match.name);
  };

  const handleSave = async () => {
    if (!ticker.trim()) return;
    setSaving(true);
    await onSave({
      ticker: ticker.toUpperCase(),
      name: name || cryptoMatch?.name || ticker.toUpperCase(),
      yahoo_ticker: yahooTicker,
      asset_type: assetType,
      target_price: parseFloat(targetPrice) || null,
      alert_price:  parseFloat(alertPrice)  || null,
      alert_direction: alertPrice ? alertDir : null,
      note: note.trim() || null,
    });
    setSaving(false);
  };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9000,
      background:"rgba(0,0,0,0.7)", backdropFilter:"blur(4px)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:20,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background:"var(--bg2)", border:"1px solid var(--border)",
        borderRadius:14, padding:"28px 28px", maxWidth:440, width:"100%",
        fontFamily:FM,
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
          <div style={{ fontSize:11, fontWeight:800, letterSpacing:"0.3em", color:"var(--text4)" }}>
            {item ? "EDIT WATCHLIST ITEM" : "ADD TO WATCHLIST"}
          </div>
          <button onClick={onClose} style={{
            background:"transparent", border:"1px solid var(--border)",
            borderRadius:6, width:28, height:28, cursor:"pointer",
            color:"var(--text4)", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center",
          }}>×</button>
        </div>

        {/* Asset type */}
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:8 }}>ASSET TYPE</div>
          <div style={{ display:"flex", gap:8 }}>
            {["stock","crypto"].map(t => (
              <button key={t} onClick={() => setAssetType(t)}
                style={{
                  flex:1, padding:"10px", borderRadius:8, cursor:"pointer",
                  border:`1.5px solid ${assetType === t ? C.orange : "var(--border)"}`,
                  background: assetType === t ? "rgba(242,135,5,0.08)" : "transparent",
                  fontFamily:FM, fontSize:11, fontWeight:700,
                  color: assetType === t ? C.orange : "var(--text3)",
                  letterSpacing:"0.1em", transition:"all 0.15s",
                }}>
                {t.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Ticker with autocomplete */}
        <div style={{ marginBottom:14, position:"relative" }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:6 }}>
            {assetType === "crypto" ? "TOKEN" : "TICKER"}
          </div>
          <input
            ref={inputRef}
            value={ticker}
            onChange={e => handleTickerChange(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
            placeholder={assetType === "crypto" ? "BTC, ETH, RUNE..." : "AAPL, MSFT, TSLA..."}
            style={{
              width:"100%", background:"var(--bg3)", border:"1px solid var(--border)",
              borderRadius:8, padding:"11px 14px", color:"var(--text)",
              fontFamily:FM, fontSize:14, fontWeight:700, outline:"none", colorScheme:"dark",
              boxSizing:"border-box",
            }}
          />
          {/* Autocomplete dropdown */}
          {showDropdown && suggestions.length > 0 && (
            <div style={{
              position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:100,
              background:"var(--bg2)", border:"1px solid var(--border)",
              borderRadius:8, overflow:"hidden",
              boxShadow:"0 8px 24px rgba(0,0,0,0.4)",
            }}>
              {suggestions.map((s, i) => (
                <div key={i}
                  onMouseDown={() => selectSuggestion(s)}
                  style={{
                    padding:"10px 14px", cursor:"pointer",
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                    borderBottom: i < suggestions.length - 1 ? "1px solid var(--border)" : "none",
                    transition:"background 0.1s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(242,135,5,0.08)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <span style={{ fontFamily:FM, fontSize:12, fontWeight:700, color:C.orange }}>
                    {s.ticker || s}
                  </span>
                  <span style={{ fontFamily:FM, fontSize:10, color:"var(--text4)" }}>
                    {s.name && s.name !== (s.ticker || s) ? s.name : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Live price indicator */}
        {(fetchingPrice || fetchedPrice) && (
          <div style={{ marginTop:6, display:"flex", alignItems:"center", gap:6 }}>
            {fetchingPrice ? (
              <div style={{ width:10, height:10, border:"1.5px solid var(--border)", borderTopColor:C.orange, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
            ) : (
              <i className="ph ph-currency-dollar" style={{ fontSize:12, color:C.orange }}/>
            )}
            <span style={{ fontFamily:FM, fontSize:11, fontWeight:700, color: fetchedPrice ? C.orange : "var(--text4)" }}>
              {fetchingPrice ? "Fetching price…" : `Live: ${fetchedPrice < 1 ? fetchedPrice.toFixed(6) : fetchedPrice < 100 ? fetchedPrice.toFixed(3) : fetchedPrice.toFixed(2)}`}
            </span>
          </div>
        )}

        {/* Name */}
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:6 }}>NAME</div>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Apple Inc."
            style={{
              width:"100%", background:"var(--bg3)", border:"1px solid var(--border)",
              borderRadius:8, padding:"11px 14px", color:"var(--text)",
              fontFamily:FM, fontSize:13, outline:"none", colorScheme:"dark",
              boxSizing:"border-box",
            }}
          />
        </div>

        {/* Target price */}
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:6 }}>
            TARGET PRICE <span style={{ color:"var(--text5)", fontWeight:400 }}>(optional)</span>
          </div>
          <input
            type="number" value={targetPrice}
            onChange={e => setTargetPrice(e.target.value)}
            placeholder="0.00"
            style={{
              width:"100%", background:"var(--bg3)", border:"1px solid var(--border)",
              borderRadius:8, padding:"11px 14px", color:"var(--text)",
              fontFamily:FM, fontSize:14, outline:"none", colorScheme:"dark",
              boxSizing:"border-box",
            }}
          />
        </div>

        {/* Alert */}
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:6 }}>
            PRICE ALERT <span style={{ color:"var(--text5)", fontWeight:400 }}>(optional)</span>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <input
              type="number" value={alertPrice}
              onChange={e => setAlertPrice(e.target.value)}
              placeholder="0.00"
              style={{
                flex:1, background:"var(--bg3)", border:"1px solid var(--border)",
                borderRadius:8, padding:"11px 14px", color:"var(--text)",
                fontFamily:FM, fontSize:14, outline:"none", colorScheme:"dark",
              }}
            />
            <div style={{ display:"flex", gap:4 }}>
              {["above","below"].map(d => (
                <button key={d} onClick={() => setAlertDir(d)}
                  style={{
                    padding:"0 12px", borderRadius:8, cursor:"pointer",
                    border:`1.5px solid ${alertDir === d ? C.orange : "var(--border)"}`,
                    background: alertDir === d ? "rgba(242,135,5,0.08)" : "transparent",
                    fontFamily:FM, fontSize:9, fontWeight:700,
                    color: alertDir === d ? C.orange : "var(--text4)",
                    letterSpacing:"0.08em",
                  }}>
                  {d === "above" ? "▲ ABOVE" : "▼ BELOW"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Note */}
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:6 }}>
            NOTE <span style={{ color:"var(--text5)", fontWeight:400 }}>(optional)</span>
          </div>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Why are you watching this asset?"
            rows={2}
            style={{
              width:"100%", background:"var(--bg3)", border:"1px solid var(--border)",
              borderRadius:8, padding:"11px 14px", color:"var(--text)",
              fontFamily:FM, fontSize:12, outline:"none", colorScheme:"dark",
              resize:"vertical", boxSizing:"border-box",
            }}
          />
        </div>

        <button onClick={handleSave} disabled={saving || !ticker.trim()}
          style={{
            width:"100%", background: C.orange, border:"none", borderRadius:10,
            padding:"14px", fontFamily:FM, fontSize:12, fontWeight:800,
            letterSpacing:"0.15em", color:"#000", cursor: saving ? "wait" : "pointer",
            opacity: saving ? 0.7 : 1, transition:"opacity 0.15s",
          }}>
          {saving ? "SAVING…" : item ? "SAVE CHANGES" : "ADD TO WATCHLIST"}
        </button>
      </div>
    </div>
  );
}

// ── Alert checker ─────────────────────────────────────────────────────────────
function checkAlert(item, livePrice) {
  if (!item.alert_price || !livePrice) return false;
  if (item.alert_direction === "above" && livePrice >= item.alert_price) return true;
  if (item.alert_direction === "below" && livePrice <= item.alert_price) return true;
  return false;
}

// ── Resize handle — standalone, used outside card ────────────────────────────
function ResizeHandle({ onDrag, currentHeight }) {
  const overlayRef = useRef(null);

  const onMouseDown = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = currentHeight;

    if (overlayRef.current) overlayRef.current.style.display = "block";

    const onMove = (ev) => {
      const newH = Math.max(200, Math.min(1200, startH + (ev.clientY - startY)));
      onDrag(Math.round(newH));
    };
    const onUp = () => {
      if (overlayRef.current) overlayRef.current.style.display = "none";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
  };

  return (
    <div style={{ position:"relative" }}>
      {/* Full-page overlay during drag to block iframes */}
      <div ref={overlayRef} style={{
        display:"none", position:"fixed",
        top:0, left:0, right:0, bottom:0,
        zIndex:9999, cursor:"ns-resize",
      }}/>
      <div onMouseDown={onMouseDown} style={{
        height:14, cursor:"ns-resize",
        background:"var(--bg3)",
        border:"1px solid var(--border)",
        borderTop:"none",
        borderRadius:"0 0 8px 8px",
        display:"flex", alignItems:"center", justifyContent:"center",
        userSelect:"none",
      }}>
        <div style={{ width:40, height:3, borderRadius:2, background:"var(--border2)" }}/>
      </div>
    </div>
  );
}

// ── TradingView chart inner — no handle, height controlled externally ─────────
function TradingViewChartInner({ ticker, assetType, height }) {
  const containerRef = useRef(null);

  const tvSymbol = assetType === "crypto"
    ? `CRYPTO:${ticker.replace("-USD","").replace("-USDT","")}USD`
    : ticker;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = "";

    const widgetDiv = document.createElement("div");
    widgetDiv.className = "tradingview-widget-container__widget";
    widgetDiv.style.width  = "100%";
    widgetDiv.style.height = "100%";
    container.appendChild(widgetDiv);

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize:            false,
      width:               "100%",
      height:              700,
      symbol:              tvSymbol,
      interval:            "D",
      timezone:            "Etc/UTC",
      theme:               "dark",
      style:               "1",
      locale:              "en",
      backgroundColor:     "rgba(0,0,0,0)",
      gridColor:           "rgba(44,44,44,0.5)",
      hide_top_toolbar:    false,
      hide_legend:         false,
      save_image:          false,
      allow_symbol_change: false,
      support_host:        "https://www.tradingview.com",
    });
    container.appendChild(script);
    return () => { container.innerHTML = ""; };
  }, [tvSymbol]);

  // Sync iframe height when height prop changes
  useEffect(() => {
    const iframe = containerRef.current?.querySelector("iframe");
    if (iframe) iframe.style.height = `${height}px`;
  }, [height]);

  return (
    <div
      ref={containerRef}
      className="tradingview-widget-container"
      style={{ height, width:"100%", background:"var(--bg3)" }}
    />
  );
}

// ── TradingView Chart with resize ─────────────────────────────────────────────
function TradingViewChart({ ticker, assetType, height, onHeightChange }) {
  const containerRef = useRef(null);
  const overlayRef   = useRef(null);

  const tvSymbol = assetType === "crypto"
    ? `CRYPTO:${ticker.replace("-USD","").replace("-USDT","")}USD`
    : ticker;

  // Build widget once per symbol with explicit initial height
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = "";

    const widgetDiv = document.createElement("div");
    widgetDiv.className = "tradingview-widget-container__widget";
    widgetDiv.style.width  = "100%";
    widgetDiv.style.height = "100%";
    container.appendChild(widgetDiv);

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize:            false,
      width:               "100%",
      height:              700,      // always start at 700
      symbol:              tvSymbol,
      interval:            "D",
      timezone:            "Etc/UTC",
      theme:               "dark",
      style:               "1",
      locale:              "en",
      backgroundColor:     "rgba(0,0,0,0)",
      gridColor:           "rgba(44,44,44,0.5)",
      hide_top_toolbar:    false,
      hide_legend:         false,
      save_image:          false,
      allow_symbol_change: false,
      support_host:        "https://www.tradingview.com",
    });
    container.appendChild(script);
    return () => { container.innerHTML = ""; };
  }, [tvSymbol]);

  // Sync iframe height without rebuilding widget
  useEffect(() => {
    const iframe = containerRef.current?.querySelector("iframe");
    if (iframe) iframe.style.height = `${height}px`;
  }, [height]);

  const onDragStart = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = containerRef.current
      ? containerRef.current.getBoundingClientRect().height
      : height;

    if (overlayRef.current) overlayRef.current.style.display = "block";

    const onMove = (ev) => {
      const newH = Math.max(200, Math.min(1200, startH + (ev.clientY - startY)));
      onHeightChange(Math.round(newH));
    };
    const onUp = () => {
      if (overlayRef.current) overlayRef.current.style.display = "none";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
  };

  return (
    <div style={{ position:"relative", userSelect:"none" }}>
      {/* Overlay blocks iframe during drag */}
      <div ref={overlayRef} style={{
        display:"none", position:"absolute",
        top:0, left:0, right:0, bottom:0, zIndex:20,
      }}/>
      <div
        ref={containerRef}
        className="tradingview-widget-container"
        style={{ height, width:"100%", background:"var(--bg3)" }}
      />
      {/* Resize handle */}
      <div onMouseDown={onDragStart} style={{
        height:12, cursor:"ns-resize",
        background:"var(--bg3)",
        borderTop:"1px solid var(--border)",
        display:"flex", alignItems:"center", justifyContent:"center",
      }}>
        <div style={{ width:36, height:3, borderRadius:2, background:"var(--border2)" }}/>
      </div>
    </div>
  );
}

export default function WatchlistPage({ supabase, user, isMobile = false, demoItems = null }) {
  const [items, setItems]         = useState([]);
  const [prices, setPrices]       = useState({});
  const [loading, setLoading]     = useState(true);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [filter, setFilter]       = useState("all"); // all | stock | crypto
  const [sortBy, setSortBy]       = useState("created"); // created | change | target
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedCharts, setExpandedCharts] = useState(new Set()); // set of open item ids
  const [chartHeights, setChartHeights]     = useState({}); // { [id]: height } — used in list mode
  const [rowHeights,   setRowHeights]       = useState({}); // { [rowIndex]: height } — used in grid mode
  const [viewMode, setViewMode]             = useState("list"); // "list" | "grid"

  // Load watchlist from Supabase (or use demo items)
  useEffect(() => {
    if (demoItems) {
      setItems(demoItems);
      setExpandedCharts(new Set(demoItems.map(i => i.id)));
      setLoading(false);
      return;
    }
    if (!user) return;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("watchlist")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (!error) {
        setItems(data || []);
        setExpandedCharts(new Set((data || []).map(i => i.id)));
      }
      setLoading(false);
    })();
  }, [user, demoItems]);

  // Fetch live prices for all items
  useEffect(() => {
    if (!items.length) return;
    (async () => {
      setPricesLoading(true);
      const results = {};
      await Promise.all(items.map(async item => {
        try {
          const ticker = item.yahoo_ticker || item.ticker;
          const res = await fetch(`/api/mstr-price?ticker=${encodeURIComponent(ticker)}&t=${Date.now()}`);
          if (res.ok) {
            const d = await res.json();
            results[item.id] = { price: d.price || 0, prev: d.prev || 0 };
          }
        } catch {}
      }));
      setPrices(results);
      setPricesLoading(false);
    })();
  }, [items.length]);

  const handleSave = async (data) => {
    if (editItem) {
      const { error } = await supabase
        .from("watchlist")
        .update(data)
        .eq("id", editItem.id);
      if (!error) setItems(prev => prev.map(i => i.id === editItem.id ? { ...i, ...data } : i));
    } else {
      const { data: inserted, error } = await supabase
        .from("watchlist")
        .insert({ ...data, user_id: user.id })
        .select()
        .single();
      if (!error && inserted) setItems(prev => [inserted, ...prev]);
    }
    setShowModal(false);
    setEditItem(null);
  };

  const handleDelete = async (id) => {
    await supabase.from("watchlist").delete().eq("id", id);
    setItems(prev => prev.filter(i => i.id !== id));
    setDeleteConfirm(null);
  };

  // Filter + sort
  const filtered = items
    .filter(i => filter === "all" ? true : i.asset_type === filter)
    .sort((a, b) => {
      if (sortBy === "change") {
        const pa = prices[a.id], pb = prices[b.id];
        const ca = pa ? ((pa.price - pa.prev) / pa.prev) * 100 : 0;
        const cb = pb ? ((pb.price - pb.prev) / pb.prev) * 100 : 0;
        return cb - ca;
      }
      if (sortBy === "target") {
        const pa = prices[a.id]?.price || 0;
        const pb = prices[b.id]?.price || 0;
        const da = a.target_price && pa ? Math.abs((a.target_price - pa) / pa) : 999;
        const db = b.target_price && pb ? Math.abs((b.target_price - pb) / pb) : 999;
        return da - db;
      }
      return 0; // created — already sorted by DB
    });

  const alertsFired = items.filter(i => checkAlert(i, prices[i.id]?.price));

  return (
    <div style={{
      minHeight:"100vh", background:"var(--bg)",
      padding: isMobile ? "20px 16px 80px" : "36px 24px 60px",
      fontFamily:FM, color:"var(--text)",
    }}>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28 }}>
        <div>
          <div style={{ fontSize:9, fontWeight:800, letterSpacing:"0.3em", color:"var(--text4)", marginBottom:6 }}>
            WATCHLIST
          </div>
          <div style={{ fontSize: isMobile ? 26 : 34, fontWeight:900, letterSpacing:"-0.02em", color:"var(--text)" }}>
            Assets to Watch
          </div>
          {alertsFired.length > 0 && (
            <div style={{
              marginTop:8, display:"inline-flex", alignItems:"center", gap:6,
              background:"rgba(242,135,5,0.1)", border:`1px solid ${C.orange}`,
              borderRadius:6, padding:"4px 10px",
            }}>
              <i className="ph ph-bell-ringing" style={{ fontSize:13, color:C.orange }}/>
              <span style={{ fontSize:9, fontWeight:700, color:C.orange, letterSpacing:"0.1em" }}>
                {alertsFired.length} ALERT{alertsFired.length > 1 ? "S" : ""} TRIGGERED
              </span>
            </div>
          )}
        </div>
        <button onClick={() => { if (demoItems) return; setEditItem(null); setShowModal(true); }}
          disabled={!!demoItems}
          style={{
            background:C.orange, border:"none", borderRadius:10,
            padding: isMobile ? "10px 16px" : "11px 20px",
            fontFamily:FM, fontSize:10, fontWeight:800,
            letterSpacing:"0.15em", color:"#000", cursor:"pointer",
            display:"flex", alignItems:"center", gap:7,
          }}>
          <i className="ph ph-plus" style={{ fontSize:14 }}/>
          {!isMobile && "ADD ASSET"}
        </button>
      </div>

      {/* Filter + Sort bar */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:10 }}>
        <div style={{ display:"flex", gap:6 }}>
          {[
            { key:"all",    label:"ALL" },
            { key:"stock",  label:"STOCKS" },
            { key:"crypto", label:"CRYPTO" },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              style={{
                background: filter === f.key ? "rgba(242,135,5,0.12)" : "transparent",
                border:`1px solid ${filter === f.key ? C.orange : "var(--border)"}`,
                borderRadius:6, padding:"5px 12px", cursor:"pointer",
                fontFamily:FM, fontSize:8, fontWeight:700,
                letterSpacing:"0.12em", color: filter === f.key ? C.orange : "var(--text4)",
                transition:"all 0.15s",
              }}>
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
          <span style={{ fontSize:9, color:"var(--text4)", letterSpacing:"0.1em" }}>SORT:</span>
          {[
            { key:"created", label:"DATE ADDED" },
            { key:"change",  label:"24H CHANGE" },
            { key:"target",  label:"TARGET DISTANCE" },
          ].map(s => (
            <button key={s.key} onClick={() => setSortBy(s.key)}
              style={{
                background: sortBy === s.key ? "rgba(242,135,5,0.08)" : "transparent",
                border:`1px solid ${sortBy === s.key ? C.orange : "var(--border)"}`,
                borderRadius:6, padding:"5px 10px", cursor:"pointer",
                fontFamily:FM, fontSize:8, fontWeight:700,
                letterSpacing:"0.1em", color: sortBy === s.key ? C.orange : "var(--text4)",
                transition:"all 0.15s",
              }}>
              {s.label}
            </button>
          ))}
          {/* View mode toggle */}
          <div style={{ display:"flex", border:"1px solid var(--border)", borderRadius:6, overflow:"hidden", marginLeft:4 }}>
            {[
              { key:"list", icon:"ph-list" },
              { key:"grid", icon:"ph-grid-four" },
            ].map(v => (
              <button key={v.key} onClick={() => setViewMode(v.key)} style={{
                background: viewMode === v.key ? "rgba(242,135,5,0.12)" : "transparent",
                border:"none", padding:"5px 9px", cursor:"pointer",
                color: viewMode === v.key ? C.orange : "var(--text4)",
                transition:"all 0.15s", display:"flex", alignItems:"center",
              }}>
                <i className={`ph ${v.icon}`} style={{ fontSize:13 }}/>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign:"center", padding:60, color:"var(--text4)", fontSize:11, letterSpacing:"0.3em" }}>
          LOADING…
        </div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div style={{
          textAlign:"center", padding:"60px 20px",
          border:"1px dashed var(--border)", borderRadius:12,
        }}>
          <i className="ph ph-eye" style={{ fontSize:40, color:"var(--text5)", display:"block", marginBottom:14 }}/>
          <div style={{ fontSize:13, fontWeight:700, color:"var(--text4)", marginBottom:6 }}>
            No assets on your watchlist
          </div>
          <div style={{ fontSize:11, color:"var(--text5)", marginBottom:20 }}>
            Add stocks or crypto you want to keep an eye on
          </div>
          <button onClick={() => setShowModal(true)}
            style={{
              background:C.orange, border:"none", borderRadius:8,
              padding:"10px 20px", fontFamily:FM, fontSize:10,
              fontWeight:800, letterSpacing:"0.15em", color:"#000", cursor:"pointer",
            }}>
            + ADD FIRST ASSET
          </button>
        </div>
      )}

      {/* Asset cards */}
      {!loading && filtered.length > 0 && (() => {
        // In grid mode — group into rows of 2, share height per row
        // In list mode — each item is its own row
        const renderCard = (item, sharedHeight, onSharedHeightChange) => {
          const p          = prices[item.id];
          const livePrice  = p?.price || 0;
          const prevClose  = p?.prev  || 0;
          const change24h  = prevClose > 0 ? ((livePrice - prevClose) / prevClose) * 100 : null;
          const isUp       = change24h !== null && change24h >= 0;
          const hasTarget  = !!item.target_price;
          const targetDist = hasTarget && livePrice > 0
            ? ((item.target_price - livePrice) / livePrice) * 100 : null;
          const alertFired = checkAlert(item, livePrice);
          const chartOpen  = expandedCharts.has(item.id);

          return (
            <div key={item.id} style={{
              background:"var(--bg2)",
              border:`1px solid ${alertFired ? "rgba(242,135,5,0.4)" : "var(--border)"}`,
              borderRadius:12, overflow:"hidden", minWidth:0,
            }}>
              {/* Info row */}
              <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", flexWrap:"wrap" }}>
                <div style={{
                  width:38, height:38, borderRadius:9, flexShrink:0,
                  background: item.asset_type === "crypto" ? "rgba(96,165,250,0.1)" : "rgba(242,135,5,0.1)",
                  border:`1px solid ${item.asset_type === "crypto" ? "rgba(96,165,250,0.2)" : "rgba(242,135,5,0.2)"}`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <i className={item.asset_type === "crypto" ? "ph ph-coins" : "ph ph-chart-line-up"}
                    style={{ fontSize:16, color: item.asset_type === "crypto" ? "#60a5fa" : C.orange }}/>
                </div>
                <div style={{ minWidth:80 }}>
                  <div style={{ fontFamily:FM, fontSize:14, fontWeight:900, color:"var(--text)", letterSpacing:"0.02em" }}>{item.ticker}</div>
                  {item.name && item.name !== item.ticker && (
                    <div style={{ fontFamily:FM, fontSize:9, color:"var(--text4)", marginTop:1 }}>{item.name}</div>
                  )}
                </div>
                <div style={{ minWidth:80 }}>
                  {pricesLoading && !livePrice ? (
                    <div style={{ width:14, height:14, border:"2px solid var(--border)", borderTopColor:C.orange, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
                  ) : (
                    <div style={{ fontFamily:FM, fontSize:14, fontWeight:700, color:"var(--text)" }}>{livePrice ? fmtPrice(livePrice) : "—"}</div>
                  )}
                  {change24h !== null && (
                    <div style={{ fontFamily:FM, fontSize:10, fontWeight:700, color: isUp ? C.green : C.red, marginTop:1 }}>{fmtPct(change24h)}</div>
                  )}
                </div>
                {!isMobile && <>
                  <div style={{ minWidth:80 }}>
                    <div style={{ fontFamily:FM, fontSize:8, color:"var(--text4)", letterSpacing:"0.1em", marginBottom:2 }}>TARGET</div>
                    {hasTarget ? (
                      <>
                        <div style={{ fontFamily:FM, fontSize:11, fontWeight:700, color:"var(--text2)" }}>{fmtPrice(item.target_price)}</div>
                        {targetDist !== null && (
                          <div style={{ fontFamily:FM, fontSize:9, fontWeight:700, color: targetDist > 0 ? C.green : C.red }}>
                            {targetDist > 0 ? "▲" : "▼"} {Math.abs(targetDist).toFixed(1)}%
                          </div>
                        )}
                      </>
                    ) : <span style={{ fontFamily:FM, fontSize:11, color:"var(--text5)" }}>—</span>}
                  </div>
                  <div style={{ minWidth:80 }}>
                    <div style={{ fontFamily:FM, fontSize:8, color:"var(--text4)", letterSpacing:"0.1em", marginBottom:2 }}>ALERT</div>
                    {item.alert_price ? (
                      <>
                        <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                          <i className={alertFired ? "ph ph-bell-ringing" : "ph ph-bell"} style={{ fontSize:11, color: alertFired ? C.orange : "var(--text4)" }}/>
                          <span style={{ fontFamily:FM, fontSize:10, fontWeight:700, color: alertFired ? C.orange : "var(--text4)" }}>{fmtPrice(item.alert_price)}</span>
                        </div>
                        <div style={{ fontFamily:FM, fontSize:8, color:"var(--text5)", letterSpacing:"0.08em" }}>{item.alert_direction?.toUpperCase()}</div>
                      </>
                    ) : <span style={{ fontFamily:FM, fontSize:11, color:"var(--text5)" }}>—</span>}
                  </div>
                  {item.note && <div style={{ flex:1, fontFamily:FM, fontSize:10, color:"var(--text4)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.note}</div>}
                </>}
                {/* Actions */}
                <div style={{ marginLeft:"auto", display:"flex", gap:6, alignItems:"center", flexShrink:0 }}>
                  <button onClick={() => setExpandedCharts(prev => { const next = new Set(prev); next.has(item.id) ? next.delete(item.id) : next.add(item.id); return next; })} style={{
                    background: chartOpen ? "rgba(242,135,5,0.1)" : "transparent",
                    border:`1px solid ${chartOpen ? C.orange : "var(--border)"}`,
                    borderRadius:6, padding:"5px 8px", cursor:"pointer",
                    color: chartOpen ? C.orange : "var(--text4)", transition:"all 0.15s",
                    display:"flex", alignItems:"center", gap:4,
                  }}>
                    <i className="ph ph-chart-line" style={{ fontSize:13 }}/>
                    <i className={`ph ph-caret-${chartOpen ? "up" : "down"}`} style={{ fontSize:10 }}/>
                  </button>
                  <button onClick={() => { setEditItem(item); setShowModal(true); }} style={{
                    background:"transparent", border:"1px solid var(--border)", borderRadius:6, padding:"5px 8px",
                    cursor:"pointer", color:"var(--text4)", transition:"all 0.15s", display:"flex", alignItems:"center",
                  }} onMouseEnter={e=>{e.currentTarget.style.borderColor=C.orange;e.currentTarget.style.color=C.orange;}} onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.color="var(--text4)";}}>
                    <i className="ph ph-pencil" style={{ fontSize:13 }}/>
                  </button>
                  {deleteConfirm === item.id ? (
                    <div style={{ display:"flex", gap:4 }}>
                      <button onClick={() => handleDelete(item.id)} style={{ background:"rgba(224,62,26,0.12)", border:"1px solid #E03E1A", color:"#E03E1A", borderRadius:6, padding:"5px 8px", cursor:"pointer", fontFamily:FM, fontSize:9, fontWeight:700 }}>YES</button>
                      <button onClick={() => setDeleteConfirm(null)} style={{ background:"transparent", border:"1px solid var(--border)", color:"var(--text4)", borderRadius:6, padding:"5px 8px", cursor:"pointer", fontFamily:FM, fontSize:9 }}>NO</button>
                    </div>
                  ) : (
                    <button onClick={() => setDeleteConfirm(item.id)} style={{
                      background:"transparent", border:"1px solid var(--border)", borderRadius:6, padding:"5px 8px",
                      cursor:"pointer", color:"var(--text4)", transition:"all 0.15s", display:"flex", alignItems:"center",
                    }} onMouseEnter={e=>{e.currentTarget.style.borderColor="#E03E1A";e.currentTarget.style.color="#E03E1A";}} onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.color="var(--text4)";}}>
                      <i className="ph ph-trash" style={{ fontSize:13 }}/>
                    </button>
                  )}
                </div>
              </div>
              {/* Chart — no handle here in grid mode, handle is on the row wrapper */}
              {chartOpen && (
                <div style={{ borderTop:"1px solid var(--border)" }}>
                  <div style={{ height:sharedHeight, background:"var(--bg3)", position:"relative" }}>
                    <TradingViewChartInner ticker={item.yahoo_ticker || item.ticker} assetType={item.asset_type} height={sharedHeight}/>
                  </div>
                </div>
              )}
            </div>
          );
        };

        if (viewMode === "list") {
          return (
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              {filtered.map(item => {
                const chartOpen = expandedCharts.has(item.id);
                const h = chartHeights[item.id] ?? 700;
                return (
                  <div key={item.id}>
                    {renderCard(item, h, (newH) => setChartHeights(prev => ({ ...prev, [item.id]: newH })))}
                    {/* Individual resize handle in list mode */}
                    {chartOpen && (
                      <ResizeHandle
                        onDrag={(newH) => setChartHeights(prev => ({ ...prev, [item.id]: newH }))}
                        currentHeight={h}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          );
        }

        // Grid mode — rows of 2, shared height per row
        const rows = [];
        for (let i = 0; i < filtered.length; i += 2) {
          rows.push(filtered.slice(i, i + 2));
        }
        return (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            {rows.map((row, rowIdx) => {
              const rowH      = rowHeights[rowIdx] ?? 700;
              const anyOpen   = row.some(item => expandedCharts.has(item.id));
              return (
                <div key={rowIdx}>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:16 }}>
                    {row.map(item => renderCard(item, rowH, null))}
                  </div>
                  {/* One shared resize handle for the entire row */}
                  {anyOpen && (
                    <ResizeHandle
                      onDrag={(newH) => setRowHeights(prev => ({ ...prev, [rowIdx]: newH }))}
                      currentHeight={rowH}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Modal */}
      {showModal && (
        <WatchlistModal
          item={editItem}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditItem(null); }}
        />
      )}
    </div>
  );
}
